import { useEffect, useRef, useState } from 'react';
import {
  Outlet,
  Route,
  Routes,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { money, totalsByCurrency, type SyncOp } from '@wr/shared';
import { ApiError, errorMessage } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, EmptyState, Notice } from '../../components/ui.js';
import { LISTS } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import type { LocalRecord } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { fromFile, openCamera } from '../capture/camera.js';
import { shortDate } from '../leads/LeadSheet.js';
import {
  listExpenses,
  loadExpense,
  readReceipt,
  type Expense,
  type ExpensePage as Page,
  type ExpenseQuery,
} from './data.js';
import { ReceiptFields, checkReceipt, toFields, today, type ReceiptDraft } from './ReceiptForm.js';

/**
 * Expenses (brief section 7.8), mounted at /expenses/*: the receipt
 * capture (camera, Choose a photo, Enter manually), the AI reading, Check
 * expense, and the recent receipts; each receipt's sheet over the list.
 */
export function ExpensesScreen() {
  return (
    <Routes>
      <Route element={<ExpensesPage />}>
        <Route index element={null} />
        <Route path=":id" element={<ExpenseSheet />} />
      </Route>
      <Route path=":id/edit" element={<EditExpense />} />
    </Routes>
  );
}

/** Receipt (phone bar, home tile), at /expenses/new: the same page, the camera first. */
export function ReceiptScreen() {
  return <ExpensesPage capture />;
}

type Stage =
  | { kind: 'idle' }
  | { kind: 'reading'; photo: Blob; url: string }
  | { kind: 'review'; photo: Blob | null; url: string | null; read: boolean | null };

const EMPTY: ReceiptDraft = {
  vendor: '',
  spent_on: '',
  currency: LISTS.expenseCurrencies[0]!,
  total: '',
  tax_gst: '',
  tax_qst: '',
  tax_vat: '',
  budget_line: '',
  event_id: '',
  client: '',
  guests: '',
  purpose: '',
};

const coarse = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

function ExpensesPage({ capture = false }: { capture?: boolean }) {
  const { save, saveScan, currentEventId } = useOffline();
  const toast = useToast();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [draft, setDraft] = useState<ReceiptDraft>(EMPTY);
  const [errors, setErrors] = useState<ReturnType<typeof checkReceipt>>({});
  // Receipt: the camera at once on a computer; on a phone, the big button opens it.
  const [camera, setCamera] = useState(() => capture && !coarse());
  const [saving, setSaving] = useState(false);
  const cameraInput = useRef<HTMLInputElement>(null);
  const pickInput = useRef<HTMLInputElement>(null);
  const saved = useRef(false);

  // A receipt being checked is not lost by Back or a menu entry.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      stage.kind !== 'idle' && !saved.current && currentLocation.pathname !== nextLocation.pathname,
  );

  // The photo's address lives as long as its receipt is being checked.
  const shown = useRef<string | null>(null);
  const showPhoto = (photo: Blob) => {
    if (shown.current) URL.revokeObjectURL(shown.current);
    shown.current = URL.createObjectURL(photo);
    return shown.current;
  };
  useEffect(
    () => () => {
      if (shown.current) URL.revokeObjectURL(shown.current);
    },
    [],
  );

  async function start(fields: Partial<ReceiptDraft> = {}) {
    const eventId = (await currentEventId()) ?? '';
    setDraft({ ...EMPTY, spent_on: today(), event_id: eventId, ...fields });
    setErrors({});
  }

  /** A photo taken or chosen: read by the AI when there is network, else typed. */
  async function took(file: Blob) {
    let photo: Blob;
    try {
      photo = await fromFile(file);
    } catch {
      toast('This file is not a photo the portal can read. Choose another one.');
      return;
    }
    const url = showPhoto(photo);
    saved.current = false;
    setStage({ kind: 'reading', photo, url });
    await start();
    try {
      const r = await readReceipt(photo);
      const text = (v: string | null) => v ?? '';
      const amount = (v: number | null) => (v === null ? '' : String(v));
      setDraft((d) => ({
        ...d,
        vendor: text(r.vendor),
        spent_on: r.spent_on ?? d.spent_on,
        currency: r.currency ?? d.currency,
        total: amount(r.total),
        tax_gst: amount(r.tax_gst),
        tax_qst: amount(r.tax_qst),
        tax_vat: amount(r.tax_vat),
        budget_line: r.budget_line ?? '',
        purpose: text(r.purpose),
      }));
      setStage({ kind: 'review', photo, url, read: true });
    } catch {
      // No network or no reading: the seller types it (manual fallback).
      setStage({ kind: 'review', photo, url, read: false });
    }
  }

  async function manual() {
    saved.current = false;
    await start();
    setStage({ kind: 'review', photo: null, url: null, read: null });
  }

  function discard() {
    saved.current = true;
    setStage({ kind: 'idle' });
    if (capture) navigate('/expenses', { replace: true });
  }

  async function submit() {
    if (stage.kind !== 'review' || saving) return;
    const found = checkReceipt(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      const op: SyncOp = {
        opId: newId(),
        entity: 'expense',
        id: newId(),
        action: 'upsert',
        changedAt: new Date().toISOString(),
        fields: { ...toFields(draft), receipt_expected: Boolean(stage.photo) },
      };
      if (stage.photo) await saveScan(stage.photo, op);
      else await save(op);
      saved.current = true;
      toast('Expense saved. It goes to accounting by itself.');
      setStage({ kind: 'idle' });
      if (capture) navigate('/expenses', { replace: true });
    } catch {
      toast('This device could not save the receipt. Nothing was lost: try again.');
    } finally {
      setSaving(false);
    }
  }

  if (stage.kind === 'reading') {
    return (
      <div className="scan-wrap">
        <h1 className="page" style={{ marginBottom: 14 }}>
          Expenses
        </h1>
        <div className="reading">
          <img src={stage.url} className="cardimg" alt="Receipt photo" />
          <div className="spinner" aria-hidden="true" />
          <p style={{ margin: 0 }}>Reading the receipt</p>
        </div>
      </div>
    );
  }

  if (stage.kind === 'review') {
    return (
      <div className="scan-wrap">
        <h1 className="page" style={{ marginBottom: 6 }}>
          Check expense
        </h1>
        <p className="lede" style={{ marginBottom: 12 }}>
          {stage.read
            ? 'Read from the receipt. Check the amounts, then assign it.'
            : stage.read === false
              ? 'The receipt could not be read now. Fill in its details and assign it.'
              : 'Fill in the receipt details and assign it.'}
        </p>
        <div className="review">
          {stage.url && <img src={stage.url} className="cardimg" alt="Receipt" />}
          <ReceiptFields value={draft} onChange={setDraft} errors={errors} />
          <div className="sticky-actions">
            <button
              type="button"
              className="btn primary huge"
              disabled={saving}
              onClick={() => void submit()}
            >
              Save expense
            </button>
            <button type="button" className="btn block" onClick={discard}>
              Discard
            </button>
          </div>
        </div>
        {blocker.state === 'blocked' && (
          <Dialog
            small
            title="Leave without saving?"
            onClose={() => blocker.reset?.()}
            footer={
              <>
                <button type="button" className="btn" autoFocus onClick={() => blocker.reset?.()}>
                  Stay
                </button>
                <button type="button" className="btn primary" onClick={() => blocker.proceed?.()}>
                  Leave without saving
                </button>
              </>
            }
          >
            <p className="note">This receipt is not saved yet.</p>
          </Dialog>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1 className="page">Expenses</h1>
        <button type="button" className="btn sm" onClick={() => void manual()}>
          Enter manually
        </button>
      </div>
      <div className="camera exp-cam">
        <button
          type="button"
          className="big"
          aria-label="Take a photo of the receipt"
          onClick={() => (coarse() ? cameraInput.current?.click() : setCamera(true))}
        >
          <Icon name="receipt" />
        </button>
        <div className="exp-cam-tx">
          <p className="scan-count" style={{ margin: 0 }}>
            Snap a receipt
          </p>
          <p style={{ margin: 0 }} className="muted">
            It goes to accounting by itself once saved.
          </p>
          <div className="actions" style={{ marginTop: 6 }}>
            <button type="button" className="btn sm" onClick={() => pickInput.current?.click()}>
              Choose a photo
            </button>
          </div>
        </div>
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void took(file);
          }}
        />
        <input
          ref={pickInput}
          type="file"
          accept="image/*"
          hidden
          aria-label="Choose a photo of the receipt"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void took(file);
          }}
        />
      </div>
      <RecentReceipts />
      {camera && (
        <ReceiptCamera
          onCancel={() => setCamera(false)}
          onPhoto={(photo) => {
            setCamera(false);
            void took(photo);
          }}
          onPick={() => {
            setCamera(false);
            pickInput.current?.click();
          }}
        />
      )}
      <Outlet />
    </>
  );
}

/** On a computer: the in-app camera ("Snap a receipt"). */
function ReceiptCamera({
  onCancel,
  onPhoto,
  onPick,
}: {
  onCancel(): void;
  onPhoto(photo: Blob): void;
  onPick(): void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let current = true;
    openCamera()
      .then((s) => {
        if (!current) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        if (video.current) {
          video.current.srcObject = s;
          void video.current.play().catch(() => undefined);
        }
        setReady(true);
      })
      .catch(() =>
        setProblem('No camera available here (or permission refused). Choose a photo instead.'),
      );
    return () => {
      current = false;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  function shoot() {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d')?.drawImage(v, 0, 0);
    canvas.toBlob((b) => b && onPhoto(b), 'image/jpeg', 0.92);
  }

  return (
    <Dialog
      title="Snap a receipt"
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={onPick}>
            Choose a photo
          </button>
          <button type="button" className="btn primary" disabled={!ready} onClick={shoot}>
            <Icon name="receipt" />
            Take the photo
          </button>
        </>
      }
    >
      <div className="vf" style={{ aspectRatio: '3/4', maxHeight: '60vh' }}>
        <video
          ref={video}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <p className="note" style={{ margin: 0 }}>
        {problem ?? 'Hold the receipt flat, fully inside the frame.'}
      </p>
    </Dialog>
  );
}

/** Where a receipt stands, as the prototype says it. */
export function expenseState(x: Expense): { text: string; ok: boolean } {
  if (x.sent_at) return { text: `Sent to accounting, ${shortDate(x.sent_at)}`, ok: true };
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { text: 'Waiting for network', ok: false };
  }
  if (x.send_error) return { text: x.send_error, ok: false };
  return { text: 'Sending…', ok: false };
}

/** Recent receipts (section 7.8): search, period, event, budget line, pages of 20, totals. */
function RecentReceipts() {
  const { user, refresh } = useSignedIn();
  const { store, counts } = useOffline();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [data, setData] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<LocalRecord[]>([]);
  const [tick, setTick] = useState(0);
  const query: ExpenseQuery = {
    q: params.get('q') ?? '',
    period: (['7', '30', '90'].includes(params.get('period') ?? '')
      ? params.get('period')
      : '90') as ExpenseQuery['period'],
    event: params.get('event') ?? 'all',
    budget: params.get('budget') ?? '',
    page: Number(params.get('page') ?? '1') || 1,
  };
  const key = JSON.stringify(query);

  useEffect(() => {
    let current = true;
    void store?.listRecords('event').then((list) => current && setEvents(list));
    return () => {
      current = false;
    };
  }, [store]);

  // A window opened meanwhile wins: its address may not be rendered yet, so
  // the search waits for the next render.
  useEffect(() => {
    if (search === query.q) return;
    const timer = setTimeout(() => {
      if (window.location.pathname === location.pathname) set('q', search);
    }, 300);
    return () => clearTimeout(timer);
  });

  useEffect(() => {
    let current = true;
    listExpenses(JSON.parse(key) as ExpenseQuery, store)
      .then((page) => {
        if (!current) return;
        setError(null);
        setData(page);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
    // counts: a receipt saved or sent refreshes the list.
  }, [key, store, refresh, counts, user.id, tick]);

  // While a receipt is on its way, the list looks again every 10 seconds.
  const waiting = data?.items.some((x) => !x.sent_at) ?? false;
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => navigator.onLine && setTick((n) => n + 1), 10_000);
    return () => clearInterval(timer);
  }, [waiting]);

  function set(name: string, value: string, empty = '') {
    const next = new URLSearchParams(params);
    if (value === empty) next.delete(name);
    else next.set(name, value);
    if (name !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const totals = data
    ? data.totals.length
      ? data.totals.map((t) => money(t.total, t.currency)).join(' + ')
      : totalsByCurrency([])
    : '';

  return (
    <>
      <h2 className="sec" style={{ marginTop: 18 }}>
        Recent receipts
      </h2>
      <p className="note" style={{ margin: '-6px 0 8px' }}>
        Each receipt is emailed to accounting with its PDF. Shown here for 90 days.
      </p>
      <div className="toolbar">
        <input
          className="grow"
          type="search"
          placeholder="Search vendor, client, purpose"
          aria-label="Search receipts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Period"
          value={query.period}
          onChange={(e) => set('period', e.target.value, '90')}
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <select
          aria-label="Event"
          value={query.event}
          onChange={(e) => set('event', e.target.value, 'all')}
        >
          <option value="all">All events</option>
          <option value="none">No event</option>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {String(e.event_code)}
            </option>
          ))}
        </select>
        <select
          aria-label="Budget line"
          value={query.budget}
          onChange={(e) => set('budget', e.target.value)}
        >
          <option value="">All budget lines</option>
          {LISTS.budgetLines.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
      </div>
      {data?.offline && (
        <Notice kind="info">No connection: the receipts kept on this device.</Notice>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      {data && (
        <p className="note" style={{ margin: '0 0 8px' }}>
          {data.total} receipt{data.total === 1 ? '' : 's'}
          {data.total ? `, ${totals}` : ''}
          {pages > 1 ? `, page ${data.page} of ${pages}` : ''}
        </p>
      )}
      {data && data.items.length === 0 && (
        <EmptyState title={data.total === 0 && !query.q ? 'No receipt yet.' : 'No receipt found.'}>
          {data.total === 0 && !query.q
            ? 'Snap one right after paying.'
            : 'Change the search or the filters.'}
        </EmptyState>
      )}
      {data && data.items.length > 0 && (
        <div className="list" aria-label="Receipts">
          {data.items.map((x) => {
            const state = expenseState(x);
            return (
              <button
                key={x.id}
                type="button"
                className="row"
                onClick={() =>
                  navigate({ pathname: `/expenses/${x.id}`, search: params.toString() })
                }
              >
                <span className="xthumb">
                  <Icon name="receipt" />
                </span>
                <span className="tx">
                  <span className="t1">{x.vendor || 'Receipt'}</span>
                  <span className="t2">
                    {[shortDate(x.spent_on), x.budget_line, x.event_code, x.client]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                  <span className={`t2${state.ok ? ' ok-tx' : ''}`}>{state.text}</span>
                </span>
                <span className="rt">
                  <b className="xamt">{money(x.total, x.currency)}</b>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {data && pages > 1 && (
        <nav className="pager" aria-label="Pages">
          <button
            type="button"
            className="btn sm"
            disabled={query.page <= 1}
            onClick={() => set('page', String(query.page - 1))}
          >
            Previous
          </button>
          <span>
            {query.page} / {pages}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={query.page >= pages}
            onClick={() => set('page', String(query.page + 1))}
          >
            Next
          </button>
        </nav>
      )}
    </>
  );
}

/** Loads a receipt, again after each change this device saves or receives. */
function useExpense(id: string) {
  const { refresh } = useSignedIn();
  const { store, counts } = useOffline();
  const [data, setData] = useState<Expense | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    loadExpense(id, store)
      .then((x) => {
        if (!current) return;
        setError(x ? null : 'This receipt does not exist, or it left the portal.');
        if (x) setData(x);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [id, store, refresh, counts]);
  return { data, error };
}

/** A receipt's photo: from the portal, or the one still on this device. */
function useReceiptPhoto(x: Expense | null) {
  const { store } = useOffline();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!x) return;
    let current = true;
    let local: string | null = null;
    void (async () => {
      const kept = await store?.photo(`expense:${x.id}`).catch(() => null);
      if (!current) return;
      if (kept) {
        local = URL.createObjectURL(kept);
        setUrl(local);
      } else if (x.has_receipt) {
        setUrl(`/api/expenses/${encodeURIComponent(x.id)}/receipt`);
      }
    })();
    return () => {
      current = false;
      if (local) URL.revokeObjectURL(local);
    };
  }, [x, store]);
  return url;
}

/** A receipt's sheet (prototype openExpense): sent receipts never change. */
function ExpenseSheet() {
  const { id = '' } = useParams();
  const { user } = useSignedIn();
  const { save } = useOffline();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: x, error } = useExpense(id);
  const photo = useReceiptPhoto(x);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const close = () => navigate({ pathname: '/expenses', search: params.toString() });

  if (!x) {
    return (
      <Dialog title="Receipt" onClose={close}>
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }

  const state = expenseState(x);
  const sent = Boolean(x.sent_at);
  const taxes = [
    ['GST/HST', x.tax_gst],
    ['QST', x.tax_qst],
    [x.currency === 'USD' ? 'Sales tax' : 'VAT/other', x.tax_vat],
  ]
    .filter(([, v]) => Number(v) > 0)
    .map(([l, v]) => `${l} ${money(v, x.currency)}`)
    .join(', ');

  async function remove() {
    await save({
      opId: newId(),
      entity: 'expense',
      id,
      action: 'delete',
      changedAt: new Date().toISOString(),
    });
    toast(`Receipt deleted: ${x!.vendor ?? 'receipt'}`);
    close();
  }

  const rows: [string, string | null | undefined][] = [
    ['Reference', x.reference ?? (sent ? null : 'Given when it is sent')],
    ['Date', shortDate(x.spent_on)],
    ['Taxes', taxes || null],
    ['Budget line', x.budget_line],
    ['Event', x.event_code],
    ['Client', x.client],
    ['Guests', x.guests],
    ['Purpose', x.purpose],
    [
      'Submitted by',
      x.submitted_by ? `${x.submitted_by} (you)` : `${user.firstName} ${user.lastName} (you)`,
    ],
    ['Sent to', x.sent_to],
  ];

  return (
    <Dialog
      title={x.vendor ?? 'Receipt'}
      onClose={close}
      footer={
        <>
          {!sent && (
            <button
              type="button"
              className="btn danger left"
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </button>
          )}
          {!sent && (
            <button
              type="button"
              className="btn"
              onClick={() =>
                navigate({ pathname: `/expenses/${id}/edit`, search: params.toString() })
              }
            >
              Edit
            </button>
          )}
          {photo && (
            <a className="btn" href={photo} download={`${x.reference ?? 'receipt'}.jpg`}>
              Download photo
            </a>
          )}
          <button type="button" className="btn" onClick={close}>
            Done
          </button>
        </>
      }
    >
      <div className="actions">
        <b style={{ fontFamily: 'var(--head)', fontSize: '2rem' }}>{money(x.total, x.currency)}</b>
        <span className={`badge ${state.ok ? 'b-ok' : 'b-off'}`}>{state.text}</span>
      </div>
      <dl className="kv">
        {rows
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt>{k}</dt>
              <dd style={k === 'Purpose' ? { whiteSpace: 'pre-wrap' } : undefined}>{v}</dd>
            </div>
          ))}
      </dl>
      {photo && <img src={photo} className="cardimg" style={{ maxHeight: 'none' }} alt="Receipt" />}
      {sent && (
        <p className="note" style={{ margin: 0 }}>
          Already sent to accounting. For a correction, contact accounting directly.
        </p>
      )}
      {confirmDelete && (
        <Dialog
          small
          title="Delete this receipt?"
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button
                type="button"
                className="btn"
                autoFocus
                onClick={() => setConfirmDelete(false)}
              >
                Keep it
              </button>
              <button type="button" className="btn danger" onClick={() => void remove()}>
                Delete
              </button>
            </>
          }
        >
          <p className="note">It is not sent to accounting.</p>
        </Dialog>
      )}
    </Dialog>
  );
}

/** Edit expense: only before it is sent (prototype: the same form, "Save changes"). */
function EditExpense() {
  const { id = '' } = useParams();
  const { data: x, error } = useExpense(id);
  if (error) return <Notice kind="error">{error}</Notice>;
  if (!x) return <div className="spinner" aria-label="Loading" />;
  if (x.sent_at) {
    return (
      <Notice kind="info">
        Already sent to accounting. For a correction, contact accounting directly.
      </Notice>
    );
  }
  return <EditForm key={x.id} expense={x} />;
}

function EditForm({ expense: x }: { expense: Expense }) {
  const { save } = useOffline();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const photo = useReceiptPhoto(x);
  const [draft, setDraft] = useState<ReceiptDraft>(() => {
    const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
    return {
      vendor: s(x.vendor),
      spent_on: s(x.spent_on),
      currency: s(x.currency) || EMPTY.currency,
      total: s(x.total),
      tax_gst: s(x.tax_gst),
      tax_qst: s(x.tax_qst),
      tax_vat: s(x.tax_vat),
      budget_line: s(x.budget_line),
      event_id: s(x.event_id),
      client: s(x.client),
      guests: s(x.guests),
      purpose: s(x.purpose),
    };
  });
  const [errors, setErrors] = useState<ReturnType<typeof checkReceipt>>({});
  const back = () =>
    navigate({ pathname: `/expenses/${x.id}`, search: params.toString() }, { replace: true });

  async function submit() {
    const found = checkReceipt(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    await save({
      opId: newId(),
      entity: 'expense',
      id: x.id,
      action: 'upsert',
      changedAt: new Date().toISOString(),
      fields: toFields(draft),
    });
    toast('Expense saved.');
    back();
  }

  return (
    <div className="scan-wrap">
      <h1 className="page" style={{ marginBottom: 6 }}>
        Edit expense
      </h1>
      <p className="lede" style={{ marginBottom: 12 }}>
        Fill in the receipt details and assign it.
      </p>
      <div className="review">
        {photo && <img src={photo} className="cardimg" alt="Receipt" />}
        <ReceiptFields value={draft} onChange={setDraft} errors={errors} />
        <div className="sticky-actions">
          <button type="button" className="btn primary huge" onClick={() => void submit()}>
            Save changes
          </button>
          <button type="button" className="btn block" onClick={back}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
